import { useState, useEffect } from 'react';
import { siBluesky } from 'simple-icons/icons';
import API from '../utils/API';
import './editLinkedOrgModal.css';

const LOBBY_TYPES = ['Lobbyists', 'Associations', 'Unions'];

function resizeImage(file, maxWidth, maxHeight) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                let w = img.width;
                let h = img.height;
                if (w > h) {
                    if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth; }
                } else {
                    if (h > maxHeight) { w = Math.round(w * maxHeight / h); h = maxHeight; }
                }
                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                canvas.toBlob(
                    blob => resolve(blob),
                    file.type === 'image/png' ? 'image/png' : 'image/jpeg',
                    0.8
                );
            };
            img.onerror = reject;
            img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function formatPhone(value) {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    const m = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return m ? `(${m[1]}) ${m[2]}-${m[3]}` : value;
}

function toProperCase(str) {
    const stops = ['of', 'and', 'the'];
    return str.toLowerCase().split(' ').map((w, i) =>
        i === 0 || !stops.includes(w) ? w.charAt(0).toUpperCase() + w.slice(1) : w
    ).join(' ');
}

export default function EditLinkedOrgModal({ org, onClose, onSaved }) {
    const [logoPreview, setLogoPreview] = useState(org.logo || null);
    const [logoError, setLogoError] = useState('');

    const [name, setName] = useState(org.organization_name || '');
    const [street, setStreet] = useState(org.street_address || '');
    const [suite, setSuite] = useState(org.suite_number || '');
    const [city, setCity] = useState(org.city || '');
    const [state, setState] = useState(org.state || '');
    const [zip, setZip] = useState(org.zip_code || '');
    const [website, setWebsite] = useState(org.website || '');
    const [phone, setPhone] = useState(formatPhone(org.main_phone) || '');
    const [lobbyFilerId, setLobbyFilerId] = useState(org.lobby_filer_id || '');

    const [linkedin, setLinkedin] = useState(org.linkedin_url || '');
    const [facebook, setFacebook] = useState(org.facebook_url || '');
    const [youtube, setYoutube] = useState(org.youtube_url || '');
    const [bluesky, setBluesky] = useState(org.bluesky_url || '');
    const [threads, setThreads] = useState(org.threads_url || '');
    const [instagram, setInstagram] = useState(org.instagram_url || '');
    const [tiktok, setTiktok] = useState(org.tiktok_url || '');

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const showLobbyId = LOBBY_TYPES.includes(org.group_type);
    const canSave = name.trim().length > 0;

    // Sync if org prop changes
    useEffect(() => {
        setLogoPreview(org.logo || null);
        setName(org.organization_name || '');
        setStreet(org.street_address || '');
        setSuite(org.suite_number || '');
        setCity(org.city || '');
        setState(org.state || '');
        setZip(org.zip_code || '');
        setWebsite(org.website || '');
        setPhone(formatPhone(org.main_phone) || '');
        setLobbyFilerId(org.lobby_filer_id || '');
        setLinkedin(org.linkedin_url || '');
        setFacebook(org.facebook_url || '');
        setYoutube(org.youtube_url || '');
        setBluesky(org.bluesky_url || '');
        setThreads(org.threads_url || '');
        setInstagram(org.instagram_url || '');
        setTiktok(org.tiktok_url || '');
    }, [org]);

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLogoError('');

        const validFormats = ['image/jpg', 'image/jpeg', 'image/png'];
        if (!validFormats.includes(file.type)) {
            setLogoError('Only .jpg, .jpeg, and .png files are accepted.');
            return;
        }

        const resized = await resizeImage(file, 450, 600);
        if (resized.size > 5 * 1024 * 1024) {
            setLogoError('Image is too large after resizing. Please choose a smaller file.');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => setLogoPreview(reader.result);
        reader.readAsDataURL(resized);

        const formData = new FormData();
        formData.append('logo', resized);
        formData.append('organization_id', org.organization_id);
        formData.append('image_type', file.type);

        try {
            await API.uploadOrganizationLogo(formData);
        } catch {
            setLogoError('Logo upload failed. Other changes will still save.');
        }
    };

    const handleSave = async () => {
        setSaveError('');
        setSaving(true);
        try {
            await API.updateOrganizationInfo({
                organization_id: org.organization_id,
                organization_name: name,
                street_address: street,
                suite_number: suite,
                city,
                state,
                zip_code: zip,
                website,
                main_phone: phone,
                lobby_filer_id: lobbyFilerId,
                linkedin_url: linkedin,
                facebook_url: facebook,
                youtube_url: youtube,
                bluesky_url: bluesky,
                threads_url: threads,
                instagram_url: instagram,
                tiktok_url: tiktok,
            });
            onSaved();
            onClose();
        } catch {
            setSaveError('Failed to save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
            onClick={onClose}
        >
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable elo-modal-dialog"
                onClick={e => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header elo-header">
                        <div>
                            <h5 className="modal-title elo-title">Edit Organization</h5>
                            <div className="elo-subtitle">{org.organization_name}</div>
                        </div>
                    </div>

                    <div className="modal-body elo-body">

                        {/* Logo */}
                        <div className="elo-logo-row">
                            <div className="elo-logo-preview">
                                {logoPreview
                                    ? <img src={logoPreview} alt="Logo" className="elo-logo-img" />
                                    : <div className="elo-logo-placeholder"><i className="bi bi-building" /></div>
                                }
                            </div>
                            <div className="elo-logo-upload">
                                <label className="elo-field-label">Logo</label>
                                <input
                                    type="file"
                                    className="form-control form-control-sm"
                                    accept=".jpg,.jpeg,.png"
                                    onChange={handleLogoChange}
                                />
                                <div className="elo-field-hint">.jpg, .jpeg, .png — auto-resized on upload</div>
                                {logoError && <div className="elo-error-text">{logoError}</div>}
                            </div>
                        </div>

                        <hr className="elo-divider" />

                        {/* Name */}
                        <div className="mb-3">
                            <label className="elo-field-label">Organization Name <span className="text-danger">*</span></label>
                            <input
                                className="form-control form-control-sm"
                                value={name}
                                onChange={e => setName(e.target.value)}
                            />
                        </div>

                        {/* Address */}
                        <div className="mb-2">
                            <label className="elo-field-label">Address</label>
                            <div className="d-flex gap-2 mb-2">
                                <input
                                    className="form-control form-control-sm flex-grow-1"
                                    placeholder="Street address"
                                    value={street}
                                    onChange={e => setStreet(toProperCase(e.target.value))}
                                />
                                <input
                                    className="form-control form-control-sm elo-suite-input"
                                    placeholder="Suite #"
                                    value={suite}
                                    onChange={e => setSuite(e.target.value)}
                                />
                            </div>
                            <div className="d-flex gap-2">
                                <input
                                    className="form-control form-control-sm flex-grow-1"
                                    placeholder="City"
                                    value={city}
                                    onChange={e => setCity(toProperCase(e.target.value))}
                                />
                                <input
                                    className="form-control form-control-sm elo-state-input"
                                    placeholder="State"
                                    value={state}
                                    onChange={e => setState(e.target.value.toUpperCase())}
                                />
                                <input
                                    className="form-control form-control-sm elo-zip-input"
                                    placeholder="ZIP"
                                    value={zip}
                                    onChange={e => setZip(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Website & Phone */}
                        <div className="d-flex gap-2 mb-2 mt-3">
                            <div className="flex-grow-1">
                                <label className="elo-field-label">Website</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text"><i className="bi bi-globe" /></span>
                                    <input
                                        className="form-control"
                                        placeholder="https://example.com"
                                        value={website}
                                        onChange={e => setWebsite(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="elo-phone-col">
                                <label className="elo-field-label">Phone</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text"><i className="bi bi-telephone" /></span>
                                    <input
                                        className="form-control"
                                        placeholder="(916) 555-0123"
                                        value={phone}
                                        onChange={e => setPhone(formatPhone(e.target.value))}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Cal-Access ID */}
                        {showLobbyId && (
                            <div className="mb-3">
                                <label className="elo-field-label">Cal-Access ID</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text"><i className="bi bi-bank" /></span>
                                    <input
                                        className="form-control"
                                        value={lobbyFilerId}
                                        onChange={e => setLobbyFilerId(e.target.value)}
                                        disabled={!!org.lobby_filer_id}
                                    />
                                </div>
                            </div>
                        )}

                        <hr className="elo-divider" />

                        {/* Socials */}
                        <label className="elo-field-label mb-2">Social Links</label>
                        <div className="row g-2">
                            <div className="col-6">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text elo-social-linkedin"><i className="bi bi-linkedin" /></span>
                                    <input className="form-control" placeholder="LinkedIn" value={linkedin} onChange={e => setLinkedin(e.target.value)} />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text elo-social-threads"><i className="bi bi-threads" /></span>
                                    <input className="form-control" placeholder="Threads" value={threads} onChange={e => setThreads(e.target.value)} />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text elo-social-facebook"><i className="bi bi-facebook" /></span>
                                    <input className="form-control" placeholder="Facebook" value={facebook} onChange={e => setFacebook(e.target.value)} />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text elo-social-instagram"><i className="bi bi-instagram" /></span>
                                    <input className="form-control" placeholder="Instagram" value={instagram} onChange={e => setInstagram(e.target.value)} />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text elo-social-youtube"><i className="bi bi-youtube" /></span>
                                    <input className="form-control" placeholder="YouTube" value={youtube} onChange={e => setYoutube(e.target.value)} />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text elo-social-tiktok"><i className="bi bi-tiktok" /></span>
                                    <input className="form-control" placeholder="TikTok" value={tiktok} onChange={e => setTiktok(e.target.value)} />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text elo-social-bluesky">
                                        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ height: '1em', fill: 'currentColor' }}>
                                            <title>{siBluesky.title}</title>
                                            <path d={siBluesky.path} />
                                        </svg>
                                    </span>
                                    <input className="form-control" placeholder="Bluesky" value={bluesky} onChange={e => setBluesky(e.target.value)} />
                                </div>
                            </div>
                        </div>

                        {saveError && (
                            <div className="alert alert-danger py-2 mt-3 small">{saveError}</div>
                        )}
                    </div>

                    <div className="modal-footer elo-footer">
                        <button className="btn elo-cancel-btn" onClick={onClose}>Cancel</button>
                        <button
                            className="btn elo-save-btn"
                            onClick={handleSave}
                            disabled={!canSave || saving}
                        >
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
